import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import {
    Briefcase,
    Trophy,
    TrendingDown,
    Hourglass,
    Ban,
    XCircle,
    Search,
    RefreshCw,
    LayoutGrid,
    List as ListIcon,
    Filter,
    ArrowRight,
    Calendar,
    Building2,
    Euro,
    Percent,
    Award,
    Globe2,
    Inbox,
    MoreVertical,
    ChevronDown,
    Pencil,
    Sparkles,
    Download as DownloadIcon,
    ArrowUpDown,
    Sliders,
    Paperclip,
    Upload as UploadIcon,
    X,
    Trash2,
    FileText,
    Loader2,
    Check,
} from "lucide-react";
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    Tooltip,
    ResponsiveContainer,
    Cell,
    PieChart,
    Pie,
} from "recharts";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Skeleton } from "@/components/ui/skeleton";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";
import {
    AlertDialog,
    AlertDialogContent,
    AlertDialogHeader,
    AlertDialogTitle,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogCancel,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import {
    listTenders,
    updateOutcome,
    uploadPliegoFn,
    deleteTenderDocument,
    documentDownloadUrl,
} from "@/lib/api";
import { TourButton } from "@/components/TourButton";
import { useTour } from "@/hooks/useTour";
import { crmTour } from "@/tours";
import { detectCommunity, COMMUNITIES } from "@/lib/communities";

const STAGES = [
    {
        key: "evaluacion",
        label: "En evaluación",
        sub: "Pendientes de adjudicación",
        icon: Hourglass,
        color: "#0055ff",
        ring: "ring-blue-200",
        bg: "from-blue-50 to-sky-50",
        chipBg: "bg-blue-100 text-blue-700",
    },
    {
        key: "won",
        label: "Adjudicadas",
        sub: "Tu empresa ganó",
        icon: Trophy,
        color: "#10b981",
        ring: "ring-emerald-200",
        bg: "from-emerald-50 to-green-50",
        chipBg: "bg-emerald-100 text-emerald-700",
    },
    {
        key: "lost",
        label: "Perdidas",
        sub: "Otro adjudicatario",
        icon: TrendingDown,
        color: "#ef4444",
        ring: "ring-rose-200",
        bg: "from-rose-50 to-red-50",
        chipBg: "bg-rose-100 text-rose-700",
    },
    {
        key: "desierta",
        label: "Desiertas",
        sub: "Sin adjudicación",
        icon: Ban,
        color: "#f59e0b",
        ring: "ring-amber-200",
        bg: "from-amber-50 to-orange-50",
        chipBg: "bg-amber-100 text-amber-700",
    },
    {
        key: "withdrawn",
        label: "Retiradas",
        sub: "Anuladas / retiradas",
        icon: XCircle,
        color: "#64748b",
        ring: "ring-slate-200",
        bg: "from-slate-50 to-gray-50",
        chipBg: "bg-slate-200 text-slate-700",
    },
];

function tenderStage(t) {
    const oc = (t?.outcome?.status || "").toLowerCase();
    if (oc === "won") return "won";
    if (oc === "lost") return "lost";
    if (oc === "desierta") return "desierta";
    if (oc === "withdrawn") return "withdrawn";
    return "evaluacion";
}

function isInPipeline(t) {
    // Show every tender that is submitted (post-presentation) OR has any outcome set.
    if (!t) return false;
    if (t.status === "submitted" || t.status === "archived") return true;
    const oc = (t?.outcome?.status || "").toLowerCase();
    if (["won", "lost", "desierta", "withdrawn"].includes(oc)) return true;
    return false;
}

function fmtMoney(n) {
    if (n == null || isNaN(Number(n))) return "—";
    return Number(n).toLocaleString("es-ES", {
        style: "currency",
        currency: "EUR",
        maximumFractionDigits: 0,
    });
}

function fmtDateShort(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleDateString("es-ES", {
            day: "2-digit",
            month: "short",
            year: "2-digit",
        });
    } catch {
        return "—";
    }
}

function discountPct(t) {
    const budget = Number(t?.budget) || 0;
    const ours = Number(t?.outcome?.our_offer_amount) || 0;
    if (!budget || !ours) return null;
    return ((budget - ours) / budget) * 100;
}

function KpiCard({ icon: Icon, label, value, hint, accent = "#0055ff", index = 0 }) {
    return (
        <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, delay: index * 0.05 }}
            className="rounded-2xl bg-white border border-slate-200/70 p-5 hover:shadow-lg hover:-translate-y-0.5 transition-all"
        >
            <div className="flex items-start justify-between gap-3">
                <div
                    className="size-10 rounded-xl flex items-center justify-center shrink-0"
                    style={{ background: `${accent}1a`, color: accent }}
                >
                    <Icon size={18} />
                </div>
                <span className="text-[10px] font-bold uppercase tracking-wider text-slate-400">
                    {label}
                </span>
            </div>
            <div className="mt-3 font-display text-3xl font-black text-slate-900 tabular-nums">
                {value}
            </div>
            {hint && (
                <div className="text-[11px] text-slate-500 mt-1">{hint}</div>
            )}
        </motion.div>
    );
}

function OutcomeDialog({ open, onOpenChange, tender, targetStage, onSaved }) {
    const [ourOffer, setOurOffer] = useState("");
    const [awarded, setAwarded] = useState("");
    const [winner, setWinner] = useState("");
    const [lessons, setLessons] = useState("");
    const [busy, setBusy] = useState(false);

    useEffect(() => {
        if (open && tender) {
            setOurOffer(
                tender?.outcome?.our_offer_amount != null
                    ? String(tender.outcome.our_offer_amount)
                    : "",
            );
            setAwarded(
                tender?.outcome?.awarded_amount != null
                    ? String(tender.outcome.awarded_amount)
                    : "",
            );
            setWinner(tender?.outcome?.winning_competitor || "");
            setLessons(tender?.outcome?.lessons_learned || "");
        }
    }, [open, tender]);

    if (!tender || !targetStage) return null;
    const stageMeta = STAGES.find((s) => s.key === targetStage);

    const save = async () => {
        setBusy(true);
        try {
            await updateOutcome(tender.id, {
                status: targetStage,
                our_offer_amount: ourOffer ? Number(ourOffer) : null,
                awarded_amount: awarded ? Number(awarded) : null,
                winning_competitor: winner.trim(),
                lessons_learned: lessons.trim(),
            });
            toast.success(`Movida a "${stageMeta?.label}".`);
            onSaved?.();
            onOpenChange(false);
        } catch (e) {
            toast.error(
                e?.response?.data?.detail || "No se pudo actualizar el resultado.",
            );
        } finally {
            setBusy(false);
        }
    };

    return (
        <AlertDialog open={open} onOpenChange={onOpenChange}>
            <AlertDialogContent
                className="max-w-lg"
                data-testid="crm-outcome-dialog"
            >
                <AlertDialogHeader>
                    <AlertDialogTitle className="flex items-center gap-2">
                        {stageMeta?.icon &&
                            React.createElement(stageMeta.icon, {
                                size: 18,
                                style: { color: stageMeta.color },
                            })}
                        Mover a{" "}
                        <span style={{ color: stageMeta?.color }}>
                            {stageMeta?.label}
                        </span>
                    </AlertDialogTitle>
                    <AlertDialogDescription className="text-slate-600">
                        <span className="font-medium text-slate-800">
                            {tender.title}
                        </span>
                        <br />
                        Añade los datos del resultado. Todo es opcional excepto el
                        estado.
                    </AlertDialogDescription>
                </AlertDialogHeader>
                <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className="text-xs font-bold uppercase tracking-wider text-slate-500">
                                Nuestra oferta (€)
                            </label>
                            <Input
                                type="number"
                                step="0.01"
                                value={ourOffer}
                                onChange={(e) => setOurOffer(e.target.value)}
                                className="mt-1 rounded-xl"
                                placeholder="125000.00"
                                data-testid="crm-our-offer-input"
                            />
                        </div>
                        <div>
                            <label className="text-xs font-bold uppercase tracking-wider text-slate-500">
                                Importe adjudicado (€)
                            </label>
                            <Input
                                type="number"
                                step="0.01"
                                value={awarded}
                                onChange={(e) => setAwarded(e.target.value)}
                                className="mt-1 rounded-xl"
                                placeholder="118500.00"
                                data-testid="crm-awarded-input"
                            />
                        </div>
                    </div>
                    {targetStage === "lost" && (
                        <div>
                            <label className="text-xs font-bold uppercase tracking-wider text-slate-500">
                                Adjudicatario / ganador
                            </label>
                            <Input
                                value={winner}
                                onChange={(e) => setWinner(e.target.value)}
                                className="mt-1 rounded-xl"
                                placeholder="Empresa ABC SL"
                                data-testid="crm-winner-input"
                            />
                        </div>
                    )}
                    <div>
                        <label className="text-xs font-bold uppercase tracking-wider text-slate-500">
                            Aprendizajes / notas
                        </label>
                        <Textarea
                            value={lessons}
                            onChange={(e) => setLessons(e.target.value)}
                            className="mt-1 rounded-xl"
                            rows={3}
                            placeholder="Qué funcionó, qué no, qué mejorar para próximas licitaciones…"
                            data-testid="crm-lessons-input"
                        />
                    </div>
                </div>
                <AlertDialogFooter>
                    <AlertDialogCancel
                        disabled={busy}
                        data-testid="crm-outcome-cancel"
                    >
                        Cancelar
                    </AlertDialogCancel>
                    <Button
                        onClick={save}
                        disabled={busy}
                        className="rounded-xl text-white"
                        style={{ background: stageMeta?.color }}
                        data-testid="crm-outcome-save"
                    >
                        {busy ? "Guardando…" : "Guardar resultado"}
                    </Button>
                </AlertDialogFooter>
            </AlertDialogContent>
        </AlertDialog>
    );
}

function TenderCard({ tender, onDragStart, onClick, onQuickEdit, onDocs }) {
    const stage = tenderStage(tender);
    const meta = STAGES.find((s) => s.key === stage);
    const community = detectCommunity(tender);
    const disc = discountPct(tender);
    const docCount = (tender.documents || []).length;

    return (
        <motion.div
            layout
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95 }}
            transition={{ duration: 0.22 }}
            draggable
            onDragStart={(e) => onDragStart(e, tender)}
            className="group rounded-xl bg-white border border-slate-200 p-3 cursor-grab active:cursor-grabbing hover:border-slate-300 hover:shadow-md transition-all"
            data-testid={`crm-card-${tender.id}`}
        >
            <div className="flex items-start gap-2">
                <div className="flex-1 min-w-0">
                    <button
                        onClick={() => onClick(tender)}
                        className="text-left w-full"
                    >
                        <div className="font-display text-sm font-bold text-slate-900 line-clamp-2 group-hover:text-[#0055ff] transition-colors">
                            {tender.title || "Sin título"}
                        </div>
                    </button>
                    <div className="text-[11px] text-slate-500 mt-1 truncate">
                        <Building2 size={10} className="inline -mt-0.5 mr-1" />
                        {tender.organo ||
                            tender.contracting_authority ||
                            "—"}
                    </div>
                </div>
                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        onDocs?.(tender);
                    }}
                    className="relative text-slate-400 hover:text-[#0055ff] shrink-0 transition-colors"
                    title={`Documentos PDF (${docCount})`}
                    data-testid={`crm-card-docs-${tender.id}`}
                >
                    <Paperclip size={13} />
                    {docCount > 0 && (
                        <span className="absolute -top-1.5 -right-1.5 bg-[#0055ff] text-white text-[8px] font-bold rounded-full min-w-[14px] h-[14px] inline-flex items-center justify-center px-1 tabular-nums">
                            {docCount}
                        </span>
                    )}
                </button>
                <button
                    onClick={(e) => {
                        e.stopPropagation();
                        onQuickEdit(tender);
                    }}
                    className="text-slate-300 hover:text-[#0055ff] shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"
                    title="Editar resultado"
                    data-testid={`crm-card-edit-${tender.id}`}
                >
                    <Pencil size={13} />
                </button>
            </div>
            <div className="mt-2 flex items-center justify-between gap-2 flex-wrap">
                <div className="font-mono text-xs font-bold text-slate-900">
                    {fmtMoney(tender.budget)}
                </div>
                {community && (
                    <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 h-[18px] rounded inline-flex items-center bg-indigo-50 text-indigo-700 border border-indigo-100">
                        {community.label}
                    </span>
                )}
            </div>
            {(stage === "won" || stage === "lost") && (
                <div className="mt-2 pt-2 border-t border-slate-100 space-y-1">
                    {tender?.outcome?.our_offer_amount != null && (
                        <div className="text-[11px] flex items-center justify-between text-slate-600">
                            <span>Nuestra oferta</span>
                            <span className="font-mono font-semibold text-slate-900">
                                {fmtMoney(tender.outcome.our_offer_amount)}
                            </span>
                        </div>
                    )}
                    {tender?.outcome?.awarded_amount != null && (
                        <div className="text-[11px] flex items-center justify-between text-slate-600">
                            <span>Adjudicación</span>
                            <span className="font-mono font-semibold" style={{ color: meta?.color }}>
                                {fmtMoney(tender.outcome.awarded_amount)}
                            </span>
                        </div>
                    )}
                    {disc != null && (
                        <div className="text-[11px] flex items-center justify-between">
                            <span className="text-slate-500">Baja</span>
                            <span
                                className="font-mono font-bold"
                                style={{ color: meta?.color }}
                            >
                                {disc.toFixed(1)}%
                            </span>
                        </div>
                    )}
                    {stage === "lost" && tender?.outcome?.winning_competitor && (
                        <div className="text-[11px] text-rose-700 truncate">
                            <Trophy size={9} className="inline -mt-0.5 mr-1" />
                            {tender.outcome.winning_competitor}
                        </div>
                    )}
                </div>
            )}
            <div className="mt-2 text-[10px] font-mono text-slate-400 flex items-center justify-between">
                <span>
                    <Calendar size={9} className="inline -mt-0.5 mr-1" />
                    {fmtDateShort(tender.deadline || tender.created_at)}
                </span>
                {tender.reference && (
                    <span className="truncate ml-2 max-w-[120px]">
                        {tender.reference}
                    </span>
                )}
            </div>
        </motion.div>
    );
}

function KanbanColumn({
    stage,
    tenders,
    onDragOver,
    onDrop,
    isDropTarget,
    onCardDragStart,
    onCardClick,
    onCardQuickEdit,
    onCardDocs,
}) {
    const Icon = stage.icon;
    const totalValue = tenders.reduce(
        (acc, t) =>
            acc +
            (Number(
                t?.outcome?.awarded_amount != null
                    ? t.outcome.awarded_amount
                    : t.budget,
            ) || 0),
        0,
    );
    return (
        <div
            onDragOver={onDragOver}
            onDrop={onDrop}
            className={`flex flex-col min-w-[300px] max-w-[330px] rounded-2xl bg-gradient-to-b ${stage.bg} border ${isDropTarget ? "border-dashed border-2 ring-4" : "border-slate-200"} ${isDropTarget ? stage.ring : ""} p-3 shrink-0 transition-all`}
            data-testid={`crm-col-${stage.key}`}
        >
            <div className="flex items-center justify-between gap-2 mb-3 px-1">
                <div className="flex items-center gap-2 min-w-0">
                    <div
                        className="size-7 rounded-lg flex items-center justify-center shrink-0"
                        style={{ background: stage.color, color: "white" }}
                    >
                        <Icon size={14} />
                    </div>
                    <div className="min-w-0">
                        <div className="font-display text-sm font-bold text-slate-900 truncate">
                            {stage.label}
                        </div>
                        <div className="text-[10px] text-slate-500 truncate">
                            {stage.sub}
                        </div>
                    </div>
                </div>
                <span
                    className={`shrink-0 ${stage.chipBg} text-xs font-bold rounded-full px-2 h-6 min-w-[24px] inline-flex items-center justify-center tabular-nums`}
                >
                    {tenders.length}
                </span>
            </div>
            {tenders.length > 0 && (
                <div className="text-[10px] font-mono text-slate-500 mb-2 px-1">
                    Total ·{" "}
                    <span className="font-bold" style={{ color: stage.color }}>
                        {fmtMoney(totalValue)}
                    </span>
                </div>
            )}
            <div className="flex-1 space-y-2 overflow-y-auto pr-1" style={{ maxHeight: "calc(100vh - 480px)" }}>
                {tenders.length === 0 ? (
                    <div className="text-center text-[11px] text-slate-400 py-10 italic">
                        Suelta una licitación aquí
                    </div>
                ) : (
                    tenders.map((t) => (
                        <TenderCard
                            key={t.id}
                            tender={t}
                            onDragStart={onCardDragStart}
                            onClick={onCardClick}
                            onQuickEdit={onCardQuickEdit}
                            onDocs={onCardDocs}
                        />
                    ))
                )}
            </div>
        </div>
    );
}

function ListView({ tenders, onCardClick, onCardQuickEdit, onCardDocs }) {
    if (tenders.length === 0) {
        return (
            <div className="rounded-2xl border-2 border-dashed border-slate-200 bg-white p-16 text-center">
                <Inbox className="mx-auto text-slate-300 mb-3" size={42} />
                <div className="font-display text-lg font-semibold text-slate-700">
                    Sin licitaciones presentadas todavía
                </div>
                <div className="text-sm text-slate-500 mt-1">
                    Aquí verás todas las licitaciones presentadas a evaluación.
                </div>
            </div>
        );
    }
    return (
        <div className="rounded-2xl bg-white border border-slate-200 overflow-hidden">
            <table className="w-full text-sm">
                <thead className="bg-slate-50 text-[10px] font-bold uppercase tracking-wider text-slate-500">
                    <tr>
                        <th className="text-left px-4 py-3">Licitación</th>
                        <th className="text-left px-4 py-3">Órgano</th>
                        <th className="text-left px-4 py-3">Comunidad</th>
                        <th className="text-right px-4 py-3">Presupuesto</th>
                        <th className="text-right px-4 py-3">Nuestra oferta</th>
                        <th className="text-right px-4 py-3">Adjudicación</th>
                        <th className="text-right px-4 py-3">Baja %</th>
                        <th className="text-center px-4 py-3">Estado</th>
                        <th className="text-right px-4 py-3" />
                    </tr>
                </thead>
                <tbody>
                    {tenders.map((t, i) => {
                        const stage = tenderStage(t);
                        const meta = STAGES.find((s) => s.key === stage);
                        const community = detectCommunity(t);
                        const disc = discountPct(t);
                        return (
                            <tr
                                key={t.id}
                                className={`border-t border-slate-100 hover:bg-slate-50/60 transition-colors ${i % 2 === 0 ? "" : "bg-slate-50/30"}`}
                                data-testid={`crm-row-${t.id}`}
                            >
                                <td className="px-4 py-3 max-w-[280px]">
                                    <button
                                        onClick={() => onCardClick(t)}
                                        className="text-left font-medium text-slate-900 hover:text-[#0055ff] line-clamp-2"
                                    >
                                        {t.title || "Sin título"}
                                    </button>
                                    {t.reference && (
                                        <div className="text-[10px] font-mono text-slate-400 mt-0.5">
                                            {t.reference}
                                        </div>
                                    )}
                                </td>
                                <td className="px-4 py-3 text-slate-600 max-w-[200px] truncate">
                                    {t.organo || t.contracting_authority || "—"}
                                </td>
                                <td className="px-4 py-3">
                                    {community && (
                                        <span className="text-[10px] font-bold uppercase tracking-wider px-2 h-5 rounded inline-flex items-center bg-indigo-50 text-indigo-700 border border-indigo-100">
                                            {community.label}
                                        </span>
                                    )}
                                </td>
                                <td className="px-4 py-3 text-right font-mono text-slate-900">
                                    {fmtMoney(t.budget)}
                                </td>
                                <td className="px-4 py-3 text-right font-mono text-slate-700">
                                    {t?.outcome?.our_offer_amount != null
                                        ? fmtMoney(t.outcome.our_offer_amount)
                                        : "—"}
                                </td>
                                <td
                                    className="px-4 py-3 text-right font-mono font-bold"
                                    style={{ color: meta?.color }}
                                >
                                    {t?.outcome?.awarded_amount != null
                                        ? fmtMoney(t.outcome.awarded_amount)
                                        : "—"}
                                </td>
                                <td
                                    className="px-4 py-3 text-right font-mono font-semibold"
                                    style={{ color: meta?.color }}
                                >
                                    {disc != null
                                        ? `${disc.toFixed(1)}%`
                                        : "—"}
                                </td>
                                <td className="px-4 py-3 text-center">
                                    <span
                                        className="inline-flex items-center gap-1 px-2 h-6 rounded-full text-[10px] font-bold uppercase tracking-wider"
                                        style={{
                                            background: `${meta?.color}1a`,
                                            color: meta?.color,
                                        }}
                                    >
                                        {meta?.label}
                                    </span>
                                </td>
                                <td className="px-4 py-3 text-right">
                                    <div className="inline-flex items-center gap-2">
                                        <button
                                            onClick={() => onCardDocs(t)}
                                            className="relative text-slate-400 hover:text-[#0055ff]"
                                            title={`Documentos PDF (${(t.documents || []).length})`}
                                            data-testid={`crm-row-docs-${t.id}`}
                                        >
                                            <Paperclip size={13} />
                                            {(t.documents || []).length > 0 && (
                                                <span className="absolute -top-1.5 -right-1.5 bg-[#0055ff] text-white text-[8px] font-bold rounded-full min-w-[14px] h-[14px] inline-flex items-center justify-center px-1 tabular-nums">
                                                    {(t.documents || []).length}
                                                </span>
                                            )}
                                        </button>
                                        <button
                                            onClick={() => onCardQuickEdit(t)}
                                            className="text-slate-400 hover:text-[#0055ff]"
                                            data-testid={`crm-row-edit-${t.id}`}
                                        >
                                            <Pencil size={13} />
                                        </button>
                                    </div>
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}

function DocsPanel({ tenderId, tenders, onClose, onTendersChanged }) {
    const tender = tenders.find((t) => t.id === tenderId);
    const inputRef = useRef(null);
    const [uploading, setUploading] = useState(false);
    const [progress, setProgress] = useState(0);
    const [confirmDelete, setConfirmDelete] = useState(null);

    if (!tender) return null;
    const docs = tender.documents || [];

    const handleFiles = async (fileList) => {
        const files = Array.from(fileList || []).filter((f) => {
            const ok =
                /\.pdf$/i.test(f.name) ||
                /\.docx?$/i.test(f.name) ||
                /\.txt$/i.test(f.name);
            return ok;
        });
        if (files.length === 0) {
            toast.error("Sube archivos PDF, DOCX o TXT.");
            return;
        }
        setUploading(true);
        setProgress(0);
        let lastTender = tender;
        try {
            for (let i = 0; i < files.length; i++) {
                lastTender = await uploadPliegoFn(
                    tender.id,
                    files[i],
                    (ev) => {
                        if (ev.total) {
                            const filePct = (ev.loaded / ev.total) * 100;
                            setProgress(
                                Math.round(
                                    (i * 100 + filePct) / files.length,
                                ),
                            );
                        }
                    },
                );
            }
            toast.success(
                `${files.length} documento${files.length > 1 ? "s" : ""} subido${files.length > 1 ? "s" : ""}.`,
            );
            onTendersChanged(lastTender);
        } catch (e) {
            toast.error(
                e?.response?.data?.detail ||
                    "Error al subir documentos.",
            );
        } finally {
            setUploading(false);
            setProgress(0);
        }
    };

    const handleDelete = async (storagePath) => {
        try {
            const updated = await deleteTenderDocument(tender.id, storagePath);
            toast.success("Documento eliminado.");
            onTendersChanged(updated);
            setConfirmDelete(null);
        } catch (e) {
            toast.error(
                e?.response?.data?.detail || "No se pudo eliminar.",
            );
        }
    };

    return (
        <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-50 bg-slate-900/60 backdrop-blur-sm flex items-center justify-end"
            onClick={onClose}
            data-testid="crm-docs-panel-overlay"
        >
            <motion.div
                initial={{ x: 480 }}
                animate={{ x: 0 }}
                exit={{ x: 480 }}
                transition={{ type: "spring", stiffness: 280, damping: 32 }}
                onClick={(e) => e.stopPropagation()}
                className="bg-white w-full max-w-md h-full overflow-y-auto shadow-2xl flex flex-col"
                data-testid="crm-docs-panel"
            >
                <div className="px-5 py-4 border-b border-slate-200 flex items-start justify-between gap-3">
                    <div className="min-w-0">
                        <div className="text-[10px] font-bold uppercase tracking-wider text-[#0055ff]">
                            Documentos PDF
                        </div>
                        <h3 className="font-display text-base font-bold text-slate-900 line-clamp-2 mt-0.5">
                            {tender.title || "Sin título"}
                        </h3>
                        <div className="text-[11px] text-slate-500 mt-1">
                            {docs.length}{" "}
                            {docs.length === 1 ? "documento" : "documentos"} ·{" "}
                            {tender.reference || "—"}
                        </div>
                    </div>
                    <button
                        onClick={onClose}
                        className="text-slate-400 hover:text-slate-900 shrink-0"
                        data-testid="crm-docs-close"
                    >
                        <X size={18} />
                    </button>
                </div>

                {/* Dropzone */}
                <div
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={(e) => {
                        e.preventDefault();
                        if (!uploading) handleFiles(e.dataTransfer.files);
                    }}
                    onClick={() => !uploading && inputRef.current?.click()}
                    className="mx-5 mt-5 rounded-xl border-2 border-dashed border-[#0055ff]/40 bg-blue-50/60 hover:bg-blue-100/60 transition-colors p-6 text-center cursor-pointer"
                    data-testid="crm-docs-dropzone"
                >
                    {uploading ? (
                        <>
                            <Loader2
                                className="mx-auto text-[#0055ff] mb-2 animate-spin"
                                size={28}
                            />
                            <div className="text-sm font-medium text-slate-800">
                                Subiendo… {progress}%
                            </div>
                            <div className="mt-2 h-1.5 bg-blue-100 rounded-full overflow-hidden">
                                <div
                                    className="h-full bg-[#0055ff] transition-all duration-200"
                                    style={{ width: `${progress}%` }}
                                />
                            </div>
                        </>
                    ) : (
                        <>
                            <UploadIcon
                                className="mx-auto text-[#0055ff] mb-2"
                                size={28}
                            />
                            <div className="text-sm font-medium text-slate-800">
                                Arrastra PDFs aquí o haz clic
                            </div>
                            <div className="text-xs text-slate-500 mt-1">
                                PDF · DOCX · TXT · hasta 25 MB cada uno
                            </div>
                        </>
                    )}
                    <input
                        ref={inputRef}
                        type="file"
                        multiple
                        accept=".pdf,.docx,.doc,.txt"
                        className="hidden"
                        onChange={(e) => handleFiles(e.target.files)}
                        data-testid="crm-docs-file-input"
                    />
                </div>

                {/* Doc list */}
                <div className="flex-1 px-5 py-4 space-y-2">
                    {docs.length === 0 ? (
                        <div className="text-center py-8">
                            <Inbox
                                className="mx-auto text-slate-300 mb-2"
                                size={32}
                            />
                            <div className="text-sm text-slate-500">
                                Esta licitación todavía no tiene documentos.
                            </div>
                        </div>
                    ) : (
                        docs.map((d) => (
                            <div
                                key={d.storage_path}
                                className="rounded-xl bg-slate-50 border border-slate-200 px-3 py-2.5 flex items-center gap-3 group hover:border-slate-300 transition-colors"
                                data-testid={`crm-docs-item-${d.storage_path}`}
                            >
                                <div className="size-8 rounded-lg bg-[#0055ff]/10 text-[#0055ff] flex items-center justify-center shrink-0">
                                    <FileText size={14} />
                                </div>
                                <a
                                    href={documentDownloadUrl(d.storage_path)}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="flex-1 min-w-0 hover:text-[#0055ff]"
                                >
                                    <div className="text-sm font-medium text-slate-900 truncate">
                                        {d.filename || "documento"}
                                    </div>
                                    {d.size_bytes != null && (
                                        <div className="text-[10px] font-mono text-slate-500 mt-0.5">
                                            {(d.size_bytes / 1024).toFixed(0)} KB
                                            {d.uploaded_at &&
                                                ` · ${fmtDateShort(d.uploaded_at)}`}
                                        </div>
                                    )}
                                </a>
                                <button
                                    onClick={() => setConfirmDelete(d)}
                                    className="text-slate-300 hover:text-red-600 shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"
                                    data-testid={`crm-docs-delete-${d.storage_path}`}
                                >
                                    <Trash2 size={14} />
                                </button>
                            </div>
                        ))
                    )}
                </div>

                <AlertDialog
                    open={!!confirmDelete}
                    onOpenChange={(o) => !o && setConfirmDelete(null)}
                >
                    <AlertDialogContent>
                        <AlertDialogHeader>
                            <AlertDialogTitle>
                                ¿Eliminar este documento?
                            </AlertDialogTitle>
                            <AlertDialogDescription>
                                Se quitará "{confirmDelete?.filename}" de esta
                                licitación. Esta acción no se puede deshacer.
                            </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                            <AlertDialogCancel data-testid="crm-docs-delete-cancel">
                                Cancelar
                            </AlertDialogCancel>
                            <Button
                                className="bg-red-600 hover:bg-red-700 text-white rounded-xl"
                                onClick={() =>
                                    confirmDelete &&
                                    handleDelete(confirmDelete.storage_path)
                                }
                                data-testid="crm-docs-delete-confirm"
                            >
                                Eliminar
                            </Button>
                        </AlertDialogFooter>
                    </AlertDialogContent>
                </AlertDialog>
            </motion.div>
        </motion.div>
    );
}


export default function Crm() {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const tour = useTour("tour:crm", crmTour, { autoStart: true });
    const [tenders, setTenders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [view, setView] = useState(
        () => searchParams.get("v") || "kanban",
    ); // kanban | list
    const [search, setSearch] = useState(() => searchParams.get("q") || "");
    const [community, setCommunity] = useState(
        () => searchParams.get("ccaa") || "",
    );
    const [organo, setOrgano] = useState(
        () => searchParams.get("org") || "",
    );
    const [period, setPeriod] = useState(
        () => searchParams.get("p") || "all",
    ); // 30d | 90d | 365d | all | custom
    const [dateFrom, setDateFrom] = useState(
        () => searchParams.get("from") || "",
    );
    const [dateTo, setDateTo] = useState(() => searchParams.get("to") || "");
    const [minBudget, setMinBudget] = useState(
        () => searchParams.get("minb") || "",
    );
    const [maxBudget, setMaxBudget] = useState(
        () => searchParams.get("maxb") || "",
    );
    const [minDiscount, setMinDiscount] = useState(
        () => searchParams.get("mind") || "",
    );
    const [maxDiscount, setMaxDiscount] = useState(
        () => searchParams.get("maxd") || "",
    );
    const [enabledStages, setEnabledStages] = useState(() => {
        const s = searchParams.get("stages");
        if (!s) return new Set(STAGES.map((x) => x.key));
        return new Set(s.split(","));
    });
    const [sortBy, setSortBy] = useState(
        () => searchParams.get("sort") || "date_desc",
    );
    const [showAdvanced, setShowAdvanced] = useState(false);
    const [outcomeDialog, setOutcomeDialog] = useState({
        open: false,
        tender: null,
        target: null,
    });
    const [docsPanel, setDocsPanel] = useState(null); // tender id or null
    const [dropTarget, setDropTarget] = useState(null);
    const draggedRef = useRef(null);

    // Persist filters in URL for shareable views.
    useEffect(() => {
        const next = {};
        if (view !== "kanban") next.v = view;
        if (search) next.q = search;
        if (community) next.ccaa = community;
        if (organo) next.org = organo;
        if (period !== "all") next.p = period;
        if (period === "custom") {
            if (dateFrom) next.from = dateFrom;
            if (dateTo) next.to = dateTo;
        }
        if (minBudget) next.minb = minBudget;
        if (maxBudget) next.maxb = maxBudget;
        if (minDiscount) next.mind = minDiscount;
        if (maxDiscount) next.maxd = maxDiscount;
        if (enabledStages.size !== STAGES.length) {
            next.stages = Array.from(enabledStages).join(",");
        }
        if (sortBy !== "date_desc") next.sort = sortBy;
        setSearchParams(next, { replace: true });
    }, [
        view,
        search,
        community,
        organo,
        period,
        dateFrom,
        dateTo,
        minBudget,
        maxBudget,
        minDiscount,
        maxDiscount,
        enabledStages,
        sortBy,
        setSearchParams,
    ]);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const all = await listTenders();
            const inPipeline = (all || []).filter(isInPipeline);
            setTenders(inPipeline);
        } catch (e) {
            console.error(e);
            toast.error("No se pudieron cargar las licitaciones.");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    // Available organos in the data (for the filter dropdown).
    const organosList = useMemo(() => {
        const set = new Map();
        tenders.forEach((t) => {
            const o = t.organo || t.contracting_authority || "";
            if (o) set.set(o, (set.get(o) || 0) + 1);
        });
        return [...set.entries()]
            .map(([k, v]) => ({ name: k, count: v }))
            .sort((a, b) => b.count - a.count);
    }, [tenders]);

    // Available communities (only those present in the data).
    const communitiesList = useMemo(() => {
        const set = new Map();
        tenders.forEach((t) => {
            const c = detectCommunity(t);
            if (c) set.set(c.key, c.label);
        });
        return COMMUNITIES.filter((c) => set.has(c.key));
    }, [tenders]);

    // Apply filters.
    const filteredTenders = useMemo(() => {
        const q = search.trim().toLowerCase();
        const now = Date.now();
        let cutoff = 0;
        if (period === "30d") cutoff = now - 30 * 86400 * 1000;
        else if (period === "90d") cutoff = now - 90 * 86400 * 1000;
        else if (period === "365d") cutoff = now - 365 * 86400 * 1000;
        const customFrom = period === "custom" && dateFrom
            ? new Date(dateFrom).getTime()
            : null;
        const customTo = period === "custom" && dateTo
            ? new Date(dateTo).getTime() + 86400000 // include the to-date day
            : null;
        const minBudgetN = minBudget ? Number(minBudget) : null;
        const maxBudgetN = maxBudget ? Number(maxBudget) : null;
        const minDiscN = minDiscount !== "" ? Number(minDiscount) : null;
        const maxDiscN = maxDiscount !== "" ? Number(maxDiscount) : null;

        const filtered = tenders.filter((t) => {
            // Stage filter
            const stage = tenderStage(t);
            if (!enabledStages.has(stage)) return false;
            if (community) {
                const c = detectCommunity(t);
                if (!c || c.key !== community) return false;
            }
            if (organo) {
                if ((t.organo || t.contracting_authority || "") !== organo)
                    return false;
            }
            const refDate = new Date(
                t.outcome?.recorded_at ||
                    t.submitted_at ||
                    t.deadline ||
                    t.updated_at ||
                    t.created_at ||
                    Date.now(),
            ).getTime();
            if (cutoff > 0 && refDate < cutoff) return false;
            if (customFrom != null && refDate < customFrom) return false;
            if (customTo != null && refDate > customTo) return false;
            if (minBudgetN != null && (Number(t.budget) || 0) < minBudgetN) return false;
            if (maxBudgetN != null && (Number(t.budget) || Infinity) > maxBudgetN) return false;
            if (minDiscN != null || maxDiscN != null) {
                const d = discountPct(t);
                if (d == null) return false;
                if (minDiscN != null && d < minDiscN) return false;
                if (maxDiscN != null && d > maxDiscN) return false;
            }
            if (q) {
                const hay =
                    `${t.title || ""} ${t.organo || ""} ${t.contracting_authority || ""} ${t.reference || ""} ${t.outcome?.winning_competitor || ""} ${t.outcome?.lessons_learned || ""} ${t.notes || ""}`.toLowerCase();
                if (!hay.includes(q)) return false;
            }
            return true;
        });

        // Sort
        const cmp = (a, b) => {
            switch (sortBy) {
                case "date_asc":
                    return (
                        new Date(a.submitted_at || a.deadline || a.created_at || 0).getTime() -
                        new Date(b.submitted_at || b.deadline || b.created_at || 0).getTime()
                    );
                case "budget_desc":
                    return (Number(b.budget) || 0) - (Number(a.budget) || 0);
                case "budget_asc":
                    return (Number(a.budget) || 0) - (Number(b.budget) || 0);
                case "discount_desc":
                    return (discountPct(b) ?? -Infinity) - (discountPct(a) ?? -Infinity);
                case "discount_asc":
                    return (discountPct(a) ?? Infinity) - (discountPct(b) ?? Infinity);
                case "date_desc":
                default:
                    return (
                        new Date(b.submitted_at || b.deadline || b.created_at || 0).getTime() -
                        new Date(a.submitted_at || a.deadline || a.created_at || 0).getTime()
                    );
            }
        };
        return filtered.sort(cmp);
    }, [
        tenders,
        search,
        community,
        organo,
        period,
        dateFrom,
        dateTo,
        minBudget,
        maxBudget,
        minDiscount,
        maxDiscount,
        enabledStages,
        sortBy,
    ]);

    // Group by stage.
    const byStage = useMemo(() => {
        const map = {};
        STAGES.forEach((s) => (map[s.key] = []));
        filteredTenders.forEach((t) => {
            const s = tenderStage(t);
            (map[s] = map[s] || []).push(t);
        });
        // Preserve the filteredTenders order — sort already applied upstream.
        return map;
    }, [filteredTenders]);

    // CSV export
    const exportCsv = useCallback(() => {
        const rows = [
            [
                "Título",
                "Expediente",
                "Órgano",
                "Comunidad",
                "Estado",
                "Presupuesto €",
                "Nuestra oferta €",
                "Importe adjudicado €",
                "Baja %",
                "Adjudicatario",
                "Fecha presentación",
                "Notas / Lecciones",
            ],
        ];
        filteredTenders.forEach((t) => {
            const stage = tenderStage(t);
            const meta = STAGES.find((s) => s.key === stage);
            const c = detectCommunity(t);
            const d = discountPct(t);
            rows.push([
                t.title || "",
                t.reference || "",
                t.organo || t.contracting_authority || "",
                c?.label || "",
                meta?.label || "",
                t.budget != null ? String(t.budget) : "",
                t.outcome?.our_offer_amount != null
                    ? String(t.outcome.our_offer_amount)
                    : "",
                t.outcome?.awarded_amount != null
                    ? String(t.outcome.awarded_amount)
                    : "",
                d != null ? d.toFixed(2) : "",
                t.outcome?.winning_competitor || "",
                t.submitted_at
                    ? new Date(t.submitted_at).toISOString().slice(0, 10)
                    : "",
                (t.outcome?.lessons_learned || "").replace(/[\r\n]+/g, " "),
            ]);
        });
        const csv = rows
            .map((r) =>
                r
                    .map((c) => {
                        const s = String(c ?? "");
                        if (/[";,\n]/.test(s)) {
                            return `"${s.replace(/"/g, '""')}"`;
                        }
                        return s;
                    })
                    .join(";"),
            )
            .join("\r\n");
        const bom = "\uFEFF"; // UTF-8 BOM for Excel
        const blob = new Blob([bom + csv], { type: "text/csv;charset=utf-8" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `crm_licitaciones_${new Date().toISOString().slice(0, 10)}.csv`;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
        toast.success(
            `Exportadas ${filteredTenders.length} licitaciones a CSV.`,
        );
    }, [filteredTenders]);

    const toggleStage = (key) => {
        setEnabledStages((prev) => {
            const next = new Set(prev);
            if (next.has(key)) {
                if (next.size > 1) next.delete(key);
            } else {
                next.add(key);
            }
            return next;
        });
    };

    // KPIs (from filtered set).
    const kpis = useMemo(() => {
        const counts = {};
        let wonAmount = 0;
        let lostAmount = 0;
        let discountSum = 0;
        let discountCount = 0;
        filteredTenders.forEach((t) => {
            const s = tenderStage(t);
            counts[s] = (counts[s] || 0) + 1;
            const award = Number(t?.outcome?.awarded_amount) || 0;
            if (s === "won") wonAmount += award || Number(t?.outcome?.our_offer_amount) || 0;
            if (s === "lost") lostAmount += Number(t.budget) || 0;
            const d = discountPct(t);
            if (d != null) {
                discountSum += d;
                discountCount += 1;
            }
        });
        const decided = (counts.won || 0) + (counts.lost || 0);
        const winRate = decided > 0 ? ((counts.won || 0) / decided) * 100 : null;
        return {
            total: filteredTenders.length,
            ...counts,
            winRate,
            wonAmount,
            lostAmount,
            avgDiscount: discountCount > 0 ? discountSum / discountCount : null,
        };
    }, [filteredTenders]);

    // Funnel chart data.
    const funnelData = useMemo(
        () =>
            STAGES.map((s) => ({
                stage: s.label,
                count: byStage[s.key]?.length || 0,
                fill: s.color,
            })),
        [byStage],
    );

    // Drag-and-drop handlers.
    const onCardDragStart = (e, tender) => {
        draggedRef.current = tender;
        e.dataTransfer.effectAllowed = "move";
        try {
            e.dataTransfer.setData("text/plain", tender.id);
        } catch {
            // some browsers
        }
    };

    const onColDragOver = (e, stageKey) => {
        e.preventDefault();
        e.dataTransfer.dropEffect = "move";
        if (dropTarget !== stageKey) setDropTarget(stageKey);
    };

    const onColDrop = async (e, stageKey) => {
        e.preventDefault();
        setDropTarget(null);
        const t = draggedRef.current;
        draggedRef.current = null;
        if (!t) return;
        const current = tenderStage(t);
        if (current === stageKey) return;

        // If moving to evaluacion, clear outcome.status (no extra data needed).
        if (stageKey === "evaluacion") {
            try {
                await updateOutcome(t.id, { status: "pending" });
                toast.success("Movida a 'En evaluación'.");
                load();
            } catch (err) {
                toast.error(
                    err?.response?.data?.detail || "No se pudo mover.",
                );
            }
            return;
        }

        // For desierta / withdrawn — just persist the status (no extra fields).
        if (stageKey === "desierta" || stageKey === "withdrawn") {
            try {
                await updateOutcome(t.id, { status: stageKey });
                const meta = STAGES.find((s) => s.key === stageKey);
                toast.success(`Movida a '${meta?.label}'.`);
                load();
            } catch (err) {
                toast.error(
                    err?.response?.data?.detail || "No se pudo mover.",
                );
            }
            return;
        }

        // For won / lost — open the dialog to capture details.
        setOutcomeDialog({ open: true, tender: t, target: stageKey });
    };

    const onCardClick = (t) => navigate(`/app/licitacion/${t.id}`);
    const onCardQuickEdit = (t) =>
        setOutcomeDialog({
            open: true,
            tender: t,
            target: tenderStage(t) === "evaluacion" ? "won" : tenderStage(t),
        });

    const hasFilters = !!(
        search ||
        community ||
        organo ||
        period !== "all" ||
        minBudget ||
        maxBudget ||
        minDiscount !== "" ||
        maxDiscount !== "" ||
        dateFrom ||
        dateTo ||
        enabledStages.size !== STAGES.length ||
        sortBy !== "date_desc"
    );
    const clearFilters = () => {
        setSearch("");
        setCommunity("");
        setOrgano("");
        setPeriod("all");
        setDateFrom("");
        setDateTo("");
        setMinBudget("");
        setMaxBudget("");
        setMinDiscount("");
        setMaxDiscount("");
        setEnabledStages(new Set(STAGES.map((s) => s.key)));
        setSortBy("date_desc");
    };

    return (
        <Layout>
            <div
                className="px-4 sm:px-6 py-8 space-y-6"
                data-testid="crm-page"
            >
                {/* Hero */}
                <motion.div
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.4 }}
                    className="flex items-end justify-between gap-4 flex-wrap"
                >
                    <div>
                        <div className="text-[10px] font-bold uppercase tracking-wider text-[#0055ff]">
                            CRM · Pipeline post-presentación
                        </div>
                        <h1 className="font-display text-4xl sm:text-5xl font-black text-slate-900 mt-1 tracking-tight">
                            CRM de licitaciones
                        </h1>
                        <p className="text-sm text-slate-600 mt-2 max-w-2xl">
                            Todas tus licitaciones presentadas, ordenadas por
                            estado. Arrastra una tarjeta a otra columna para
                            cambiar el resultado y registrar la baja.
                        </p>
                    </div>
                    <div className="flex gap-2 items-center">
                        <Button
                            variant="outline"
                            onClick={load}
                            className="rounded-xl"
                            data-testid="crm-refresh-btn"
                        >
                            <RefreshCw size={14} className="mr-2" />
                            Refrescar
                        </Button>
                        <div className="rounded-xl border border-slate-200 bg-white p-1 inline-flex gap-1">
                            <button
                                onClick={() => setView("kanban")}
                                className={`px-3 h-8 rounded-lg text-xs font-semibold inline-flex items-center gap-1.5 transition-all ${view === "kanban" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-50"}`}
                                data-testid="crm-view-kanban"
                            >
                                <LayoutGrid size={13} />
                                Kanban
                            </button>
                            <button
                                onClick={() => setView("list")}
                                className={`px-3 h-8 rounded-lg text-xs font-semibold inline-flex items-center gap-1.5 transition-all ${view === "list" ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-50"}`}
                                data-testid="crm-view-list"
                            >
                                <ListIcon size={13} />
                                Lista
                            </button>
                        </div>
                    </div>
                </motion.div>

                {/* KPI strip */}
                <div
                    className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3"
                    data-testid="crm-kpis"
                >
                    <KpiCard
                        icon={Briefcase}
                        label="Total"
                        value={kpis.total}
                        hint="Presentadas"
                        accent="#0055ff"
                        index={0}
                    />
                    <KpiCard
                        icon={Hourglass}
                        label="En evaluación"
                        value={kpis.evaluacion || 0}
                        hint="Pendientes"
                        accent="#0055ff"
                        index={1}
                    />
                    <KpiCard
                        icon={Trophy}
                        label="Ganadas"
                        value={kpis.won || 0}
                        hint={
                            kpis.winRate != null
                                ? `Win rate ${kpis.winRate.toFixed(0)}%`
                                : "Sin resoluciones"
                        }
                        accent="#10b981"
                        index={2}
                    />
                    <KpiCard
                        icon={TrendingDown}
                        label="Perdidas"
                        value={kpis.lost || 0}
                        hint={fmtMoney(kpis.lostAmount) + " presup."}
                        accent="#ef4444"
                        index={3}
                    />
                    <KpiCard
                        icon={Euro}
                        label="Ganado €"
                        value={fmtMoney(kpis.wonAmount)}
                        hint="Adjudicado a ti"
                        accent="#0a8754"
                        index={4}
                    />
                    <KpiCard
                        icon={Percent}
                        label="Baja media"
                        value={
                            kpis.avgDiscount != null
                                ? `${kpis.avgDiscount.toFixed(1)}%`
                                : "—"
                        }
                        hint="Sobre presupuesto"
                        accent="#7c3aed"
                        index={5}
                    />
                </div>

                {/* Toolbar */}
                <div
                    className="rounded-2xl bg-white border border-slate-200 p-3"
                    data-testid="crm-toolbar"
                >
                    <div className="flex flex-wrap items-center gap-2">
                    <div className="relative flex-1 min-w-[200px]">
                        <Search
                            size={14}
                            className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                        />
                        <Input
                            placeholder="Buscar por título, órgano, expediente…"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            className="pl-9 rounded-xl bg-slate-50 border-slate-200"
                            data-testid="crm-search"
                        />
                    </div>
                    {/* Community filter */}
                    <Popover>
                        <PopoverTrigger asChild>
                            <Button
                                variant="outline"
                                className="rounded-xl"
                                data-testid="crm-filter-community"
                            >
                                <Globe2 size={13} className="mr-1.5" />
                                {community
                                    ? communitiesList.find((c) => c.key === community)?.label ||
                                      "Comunidad"
                                    : "Comunidad"}
                                <ChevronDown size={12} className="ml-1.5" />
                            </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-56 p-1">
                            <button
                                onClick={() => setCommunity("")}
                                className={`w-full text-left px-3 py-1.5 text-sm rounded-lg hover:bg-slate-50 ${!community ? "font-bold text-[#0055ff]" : ""}`}
                                data-testid="crm-filter-community-all"
                            >
                                Todas
                            </button>
                            {communitiesList.map((c) => (
                                <button
                                    key={c.key}
                                    onClick={() => setCommunity(c.key)}
                                    className={`w-full text-left px-3 py-1.5 text-sm rounded-lg hover:bg-slate-50 ${community === c.key ? "font-bold text-[#0055ff]" : ""}`}
                                    data-testid={`crm-filter-community-${c.key}`}
                                >
                                    {c.label}
                                </button>
                            ))}
                        </PopoverContent>
                    </Popover>
                    {/* Organo filter */}
                    <Popover>
                        <PopoverTrigger asChild>
                            <Button
                                variant="outline"
                                className="rounded-xl max-w-[260px]"
                                data-testid="crm-filter-organo"
                            >
                                <Building2 size={13} className="mr-1.5" />
                                <span className="truncate">
                                    {organo || "Órgano"}
                                </span>
                                <ChevronDown size={12} className="ml-1.5" />
                            </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-80 p-1 max-h-[360px] overflow-y-auto">
                            <button
                                onClick={() => setOrgano("")}
                                className={`w-full text-left px-3 py-1.5 text-sm rounded-lg hover:bg-slate-50 ${!organo ? "font-bold text-[#0055ff]" : ""}`}
                            >
                                Todos
                            </button>
                            {organosList.map((o) => (
                                <button
                                    key={o.name}
                                    onClick={() => setOrgano(o.name)}
                                    className={`w-full text-left px-3 py-1.5 text-sm rounded-lg hover:bg-slate-50 flex items-center justify-between gap-2 ${organo === o.name ? "font-bold text-[#0055ff]" : ""}`}
                                >
                                    <span className="truncate">{o.name}</span>
                                    <span className="font-mono text-[10px] text-slate-400 shrink-0">
                                        {o.count}
                                    </span>
                                </button>
                            ))}
                        </PopoverContent>
                    </Popover>
                    <Popover>
                        <PopoverTrigger asChild>
                            <Button
                                variant="outline"
                                className="rounded-xl"
                                data-testid="crm-filter-period"
                            >
                                <Calendar size={13} className="mr-1.5" />
                                {{
                                    "30d": "Último mes",
                                    "90d": "Últimos 3 meses",
                                    "365d": "Último año",
                                    all: "Histórico completo",
                                    custom: dateFrom || dateTo
                                        ? `${dateFrom || "…"} → ${dateTo || "…"}`
                                        : "Rango personalizado",
                                }[period]}
                                <ChevronDown size={12} className="ml-1.5" />
                            </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-64 p-1">
                            {[
                                ["30d", "Último mes"],
                                ["90d", "Últimos 3 meses"],
                                ["365d", "Último año"],
                                ["all", "Histórico completo"],
                                ["custom", "Rango personalizado…"],
                            ].map(([k, l]) => (
                                <button
                                    key={k}
                                    onClick={() => setPeriod(k)}
                                    className={`w-full text-left px-3 py-1.5 text-sm rounded-lg hover:bg-slate-50 ${period === k ? "font-bold text-[#0055ff]" : ""}`}
                                >
                                    {l}
                                </button>
                            ))}
                            {period === "custom" && (
                                <div className="mt-2 p-2 border-t border-slate-100 space-y-2">
                                    <div>
                                        <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                                            Desde
                                        </label>
                                        <Input
                                            type="date"
                                            value={dateFrom}
                                            onChange={(e) =>
                                                setDateFrom(e.target.value)
                                            }
                                            className="h-8 rounded-lg text-xs mt-1"
                                            data-testid="crm-date-from"
                                        />
                                    </div>
                                    <div>
                                        <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                                            Hasta
                                        </label>
                                        <Input
                                            type="date"
                                            value={dateTo}
                                            onChange={(e) =>
                                                setDateTo(e.target.value)
                                            }
                                            className="h-8 rounded-lg text-xs mt-1"
                                            data-testid="crm-date-to"
                                        />
                                    </div>
                                </div>
                            )}
                        </PopoverContent>
                    </Popover>
                    {/* Sort */}
                    <Popover>
                        <PopoverTrigger asChild>
                            <Button
                                variant="outline"
                                className="rounded-xl"
                                data-testid="crm-sort"
                            >
                                <ArrowUpDown size={13} className="mr-1.5" />
                                {{
                                    date_desc: "Más recientes",
                                    date_asc: "Más antiguas",
                                    budget_desc: "Importe ↓",
                                    budget_asc: "Importe ↑",
                                    discount_desc: "Baja % ↓",
                                    discount_asc: "Baja % ↑",
                                }[sortBy]}
                                <ChevronDown size={12} className="ml-1.5" />
                            </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-56 p-1">
                            {[
                                ["date_desc", "Más recientes primero"],
                                ["date_asc", "Más antiguas primero"],
                                ["budget_desc", "Mayor importe primero"],
                                ["budget_asc", "Menor importe primero"],
                                ["discount_desc", "Mayor baja % primero"],
                                ["discount_asc", "Menor baja % primero"],
                            ].map(([k, l]) => (
                                <button
                                    key={k}
                                    onClick={() => setSortBy(k)}
                                    className={`w-full text-left px-3 py-1.5 text-sm rounded-lg hover:bg-slate-50 ${sortBy === k ? "font-bold text-[#0055ff]" : ""}`}
                                >
                                    {l}
                                </button>
                            ))}
                        </PopoverContent>
                    </Popover>
                    {/* Advanced toggle */}
                    <Button
                        variant={showAdvanced ? "default" : "outline"}
                        onClick={() => setShowAdvanced((v) => !v)}
                        className={`rounded-xl ${
                            showAdvanced
                                ? "bg-slate-900 text-white hover:bg-slate-800"
                                : ""
                        }`}
                        data-testid="crm-toggle-advanced"
                    >
                        <Sliders size={13} className="mr-1.5" />
                        Avanzado
                    </Button>
                    {/* Export */}
                    <Button
                        variant="outline"
                        onClick={exportCsv}
                        disabled={filteredTenders.length === 0}
                        className="rounded-xl"
                        data-testid="crm-export-csv"
                    >
                        <DownloadIcon size={13} className="mr-1.5" />
                        CSV
                    </Button>
                    {hasFilters && (
                        <Button
                            variant="ghost"
                            onClick={clearFilters}
                            className="rounded-xl text-slate-500"
                            data-testid="crm-clear-filters"
                        >
                            <Filter size={12} className="mr-1" />
                            Limpiar
                        </Button>
                    )}
                    </div>

                    {/* Advanced filters panel */}
                    <AnimatePresence initial={false}>
                        {showAdvanced && (
                            <motion.div
                                initial={{ height: 0, opacity: 0 }}
                                animate={{ height: "auto", opacity: 1 }}
                                exit={{ height: 0, opacity: 0 }}
                                transition={{ duration: 0.22 }}
                                className="overflow-hidden"
                                data-testid="crm-advanced-panel"
                            >
                                <div className="mt-3 pt-3 border-t border-slate-100 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
                                    <div>
                                        <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                                            Importe mínimo €
                                        </label>
                                        <Input
                                            type="number"
                                            placeholder="0"
                                            value={minBudget}
                                            onChange={(e) =>
                                                setMinBudget(e.target.value)
                                            }
                                            className="mt-1 h-9 rounded-lg"
                                            data-testid="crm-min-budget"
                                        />
                                    </div>
                                    <div>
                                        <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                                            Importe máximo €
                                        </label>
                                        <Input
                                            type="number"
                                            placeholder="∞"
                                            value={maxBudget}
                                            onChange={(e) =>
                                                setMaxBudget(e.target.value)
                                            }
                                            className="mt-1 h-9 rounded-lg"
                                            data-testid="crm-max-budget"
                                        />
                                    </div>
                                    <div>
                                        <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                                            Baja mínima %
                                        </label>
                                        <Input
                                            type="number"
                                            step="0.1"
                                            placeholder="0"
                                            value={minDiscount}
                                            onChange={(e) =>
                                                setMinDiscount(e.target.value)
                                            }
                                            className="mt-1 h-9 rounded-lg"
                                            data-testid="crm-min-discount"
                                        />
                                    </div>
                                    <div>
                                        <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                                            Baja máxima %
                                        </label>
                                        <Input
                                            type="number"
                                            step="0.1"
                                            placeholder="100"
                                            value={maxDiscount}
                                            onChange={(e) =>
                                                setMaxDiscount(e.target.value)
                                            }
                                            className="mt-1 h-9 rounded-lg"
                                            data-testid="crm-max-discount"
                                        />
                                    </div>
                                    <div className="md:col-span-2 lg:col-span-4">
                                        <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                                            Estados visibles
                                        </label>
                                        <div className="mt-1 flex flex-wrap gap-1.5">
                                            {STAGES.map((s) => {
                                                const active = enabledStages.has(
                                                    s.key,
                                                );
                                                return (
                                                    <button
                                                        key={s.key}
                                                        onClick={() =>
                                                            toggleStage(s.key)
                                                        }
                                                        className={`inline-flex items-center gap-1.5 px-2.5 h-7 rounded-lg text-xs font-bold uppercase tracking-wider transition-all ${
                                                            active
                                                                ? "text-white shadow-sm"
                                                                : "text-slate-400 bg-slate-100 hover:bg-slate-200"
                                                        }`}
                                                        style={
                                                            active
                                                                ? {
                                                                      background:
                                                                          s.color,
                                                                  }
                                                                : undefined
                                                        }
                                                        data-testid={`crm-stage-toggle-${s.key}`}
                                                    >
                                                        {active && (
                                                            <Check
                                                                size={11}
                                                            />
                                                        )}
                                                        {s.label}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </div>
                            </motion.div>
                        )}
                    </AnimatePresence>
                </div>

                {/* Main view */}
                {loading ? (
                    <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-3">
                        {[...Array(5)].map((_, i) => (
                            <Skeleton key={i} className="h-64 rounded-2xl" />
                        ))}
                    </div>
                ) : view === "kanban" ? (
                    <div
                        className="flex gap-3 overflow-x-auto pb-2"
                        data-testid="crm-kanban"
                    >
                        {STAGES.map((s) => (
                            <KanbanColumn
                                key={s.key}
                                stage={s}
                                tenders={byStage[s.key] || []}
                                onDragOver={(e) => onColDragOver(e, s.key)}
                                onDrop={(e) => onColDrop(e, s.key)}
                                isDropTarget={dropTarget === s.key}
                                onCardDragStart={onCardDragStart}
                                onCardClick={onCardClick}
                                onCardQuickEdit={onCardQuickEdit}
                                onCardDocs={(t) => setDocsPanel(t.id)}
                            />
                        ))}
                    </div>
                ) : (
                    <ListView
                        tenders={filteredTenders}
                        onCardClick={onCardClick}
                        onCardQuickEdit={onCardQuickEdit}
                        onCardDocs={(t) => setDocsPanel(t.id)}
                    />
                )}

                {/* Funnel chart */}
                {filteredTenders.length > 0 && (
                    <motion.div
                        initial={{ opacity: 0, y: 8 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.4 }}
                        className="rounded-2xl bg-white border border-slate-200 p-5"
                    >
                        <div className="flex items-center gap-2 mb-3">
                            <Sparkles size={14} className="text-[#0055ff]" />
                            <div className="font-display text-base font-bold">
                                Embudo del pipeline
                            </div>
                            <span className="text-xs text-slate-500 ml-2">
                                Distribución actual ·{" "}
                                {filteredTenders.length} licitaciones
                            </span>
                        </div>
                        <div className="h-[180px]">
                            <ResponsiveContainer width="100%" height="100%">
                                <BarChart
                                    data={funnelData}
                                    margin={{ top: 12, right: 12, left: 0, bottom: 0 }}
                                >
                                    <XAxis
                                        dataKey="stage"
                                        tick={{ fontSize: 11, fill: "#64748b" }}
                                        tickLine={false}
                                        axisLine={false}
                                    />
                                    <YAxis
                                        allowDecimals={false}
                                        tick={{ fontSize: 11, fill: "#64748b" }}
                                        tickLine={false}
                                        axisLine={false}
                                    />
                                    <Tooltip
                                        cursor={{ fill: "rgba(0,85,255,0.04)" }}
                                        contentStyle={{
                                            background: "#0f172a",
                                            border: "none",
                                            borderRadius: 10,
                                            color: "white",
                                            fontSize: 12,
                                        }}
                                        labelStyle={{ color: "white" }}
                                    />
                                    <Bar dataKey="count" radius={[8, 8, 0, 0]}>
                                        {funnelData.map((entry, i) => (
                                            <Cell key={i} fill={entry.fill} />
                                        ))}
                                    </Bar>
                                </BarChart>
                            </ResponsiveContainer>
                        </div>
                    </motion.div>
                )}
            </div>

            <OutcomeDialog
                open={outcomeDialog.open}
                onOpenChange={(o) =>
                    setOutcomeDialog((prev) => ({ ...prev, open: o }))
                }
                tender={outcomeDialog.tender}
                targetStage={outcomeDialog.target}
                onSaved={load}
            />

            <AnimatePresence>
                {docsPanel && (
                    <DocsPanel
                        key={docsPanel}
                        tenderId={docsPanel}
                        tenders={tenders}
                        onClose={() => setDocsPanel(null)}
                        onTendersChanged={(updated) => {
                            // Patch the tender in place so the badge count
                            // refreshes immediately without a full reload.
                            setTenders((prev) =>
                                prev.map((t) =>
                                    t.id === updated.id ? updated : t,
                                ),
                            );
                        }}
                    />
                )}
            </AnimatePresence>

            <TourButton onClick={tour.start} label="Ver tour" />
        </Layout>
    );
}
